import {
	Box,
	Card,
	Chip,
	Grid,
	IconButton,
	Stack,
	Typography,
	Popper,
	ClickAwayListener,
} from '@mui/material';

import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { ReactComponent as Comment } from 'common/assets/images/comment.svg';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import Banner from 'common/components/Banner/Banner';
import ButtonComp from 'common/components/Button/ButtonComp';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import StepperProgressBar from 'common/components/StepperProgressBar/StepperProgressBar';
import { PROGRESS_BAR_ELEMENTS } from 'common/constants/CitationDetails';
import { RootState } from 'common/store/AppStore';
import {
	setCommentCount,
	setIsOpenInvententoryCommentDrawer,
} from 'common/store/reducer/regulationInventoryReducer';
import {
	dateInCorrectFormatWithFullYear,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { IBusinessUnitMappingEntity } from 'features/RegulationInventory/api/type-def';
import TaskComment from 'features/RiskProfile/Taxonomy/RelationMapping/MappingDetail/Tasks/TaskComment';

import detailsStyles from './RegulationDetailsHeader.module.scss';

interface IRegulationDetailsHeader {
	taskId: string;
	title: string;
	jurisdiction: string;
	assignedUserName: string;
	reviewByDate: string;
	status: string;
	mappingData?: IBusinessUnitMappingEntity[]; /// For mapping and complete
}

const {
	NOTSTARTED,
	APPLICABILITY_ASSESSMENT,
	APPLICABILITY_REVIEW,
	GROUPING_ASSESSMENT,
	GROUPING_REVIEW,
	SUMMARY_ASSESSMENT,
	SUMMARY_REVIEW,
	MAPPING_ASSESSMENT,
	MAPPING_REVIEW,
	COMPLETE,
} = PROGRESS_BAR_ELEMENTS;

const progressBarElements = [
	{
		index: 0,
		value: 3,
		label: NOTSTARTED,
	},
	{
		index: 1,
		value: 13,
		label: APPLICABILITY_ASSESSMENT,
	},
	{
		index: 2,
		value: 25,
		label: APPLICABILITY_REVIEW,
	},
	{
		index: 3,
		value: 37,
		label: GROUPING_ASSESSMENT,
	},
	{
		index: 4,
		value: 48,
		label: GROUPING_REVIEW,
	},
	{
		index: 5,
		value: 59,
		label: SUMMARY_ASSESSMENT,
	},
	{
		index: 6,
		value: 70,
		label: SUMMARY_REVIEW,
	},
	{
		index: 7,
		value: 80,
		label: MAPPING_ASSESSMENT,
	},
	{
		index: 8,
		value: 90,
		label: MAPPING_REVIEW,
	},
	{
		index: 9,
		value: 98,
		label: COMPLETE,
	},
];

const RegulationDetailsHeader = (props: IRegulationDetailsHeader) => {
	const {
		taskId,
		title,
		jurisdiction,
		assignedUserName,
		reviewByDate,
		status,
	} = props;
	const { groupId } = useParams();
	const commentCount = useSelector(
		(state: RootState) => state.regulationInventory.commentCount,
	);
	const isCommentOpen = useSelector(
		(state: RootState) =>
			state.regulationInventory.isOpenInvententoryCommentDrawer,
	);
	const { drawerCloseIconColor, commentIconColor } = detailsStyles;
	const dispatch = useDispatch();

	const onClickComment = () => {
		dispatch(setIsOpenInvententoryCommentDrawer(true));
	};
	const handleDrawerChange = () => {
		dispatch(setIsOpenInvententoryCommentDrawer(false));
		dispatch(setCommentCount(0));
	};
	const [anchorEl] = useState<null | HTMLElement>(null);
	const [assignedToAnchorEl, setAssignedToAnchorEl] =
		useState<null | HTMLElement>(null);
	const [businessUnitAnchorEl, setBusinessUnitAnchorEl] =
		useState<null | HTMLElement>(null);

	const handleClickAssignedTo = (event: React.MouseEvent<HTMLElement>) => {
		setAssignedToAnchorEl(assignedToAnchorEl ? null : event.currentTarget);
	};

	const handleClickBusinessUnit = (event: React.MouseEvent<HTMLElement>) => {
		setBusinessUnitAnchorEl(businessUnitAnchorEl ? null : event.currentTarget);
	};

	const handleClickAwayBusinessUnit = () => {
		if (assignedToAnchorEl) {
			setAssignedToAnchorEl(null);
		}
		if (businessUnitAnchorEl) {
			setBusinessUnitAnchorEl(null);
		}
	};

	const open = Boolean(anchorEl);
	const id = open ? 'simple-popper' : undefined;
	const mappingStatus =
		(groupId && status === MAPPING_ASSESSMENT) ||
		status === MAPPING_REVIEW ||
		status === COMPLETE;

	return (
		<>
			<Banner>
				<Grid
					container
					spacing={1}
					p={2}
					className={detailsStyles['reg-details-container']}
				>
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						className={detailsStyles['inventory-task-name']}
					>
						{title}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<ClickAwayListener onClickAway={handleClickAwayBusinessUnit}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="flex-start"
								spacing={2}
							>
								<Typography
									className={detailsStyles['inventory-citation-info']}
								>
									Jurisdiction: {jurisdiction}
								</Typography>
								{mappingStatus === false ? (
									<Typography
										className={detailsStyles['inventory-citation-info']}
									>
										Assigned to:{' '}
										<span
											className={detailsStyles['inventory-assaigned-to-info']}
										>
											{assignedUserName}
										</span>
									</Typography>
								) : (
									<>
										<Typography
											className={detailsStyles['inventory-citation-info']}
										>
											Owner:{' '}
											<span
												className={detailsStyles['inventory-assaigned-to-info']}
											>
												{assignedUserName}
											</span>
										</Typography>
										<Typography
											className={detailsStyles['inventory-citation-info']}
										>
											<Chip
												className={detailsStyles['chip-assigned']}
												data-testid="bu-assigned-to"
												size="small"
												label={`Assigned to: ${
													props?.mappingData?.length || 0
												}`}
												aria-describedby={id}
												onClick={handleClickAssignedTo}
											/>
											<Popper
												id={id}
												open={Boolean(assignedToAnchorEl)}
												anchorEl={assignedToAnchorEl}
											>
												<Card className={detailsStyles['box-style']}>
													<Box
														className={detailsStyles['business-unit-header']}
													>
														Assigned to
													</Box>
													{props?.mappingData &&
														props?.mappingData?.map((data) => (
															<Box
																key={data.assignedUserName}
																className={detailsStyles['bu-assigned-user']}
															>
																{`${data.assignedUserName}`}
															</Box>
														))}
												</Card>
											</Popper>
										</Typography>
									</>
								)}
								<Typography
									className={detailsStyles['inventory-citation-info']}
								>
									{toSentenceCase('Due Date')}:{' '}
									{dateInCorrectFormatWithFullYear(reviewByDate || '')}
								</Typography>
								{mappingStatus && (
									<Typography>
										<>
											<Chip
												className={detailsStyles['chip-assigned']}
												data-testid="bu-assignedname"
												label={`Business units: ${
													props?.mappingData?.length || 0
												}`}
												size="small"
												aria-describedby={id}
												onClick={handleClickBusinessUnit}
											/>
											<Popper
												id={id}
												open={Boolean(businessUnitAnchorEl)}
												anchorEl={businessUnitAnchorEl}
											>
												<Card className={detailsStyles['box-style']}>
													<Box
														className={detailsStyles['business-unit-header']}
														data-testid="bu-assigned-name-label"
													>
														{toSentenceCase('Business Units')}
													</Box>
													{props?.mappingData &&
														props?.mappingData?.map((data) => (
															<Box
																key={data.businessUnitName}
																className={
																	detailsStyles['business-unit-status']
																}
															>
																<span
																	className={detailsStyles['business-unitname']}
																>
																	{data.businessUnitName}:
																</span>
																<Chip
																	label={data.taskStatus}
																	size="small"
																	aria-describedby={id}
																	className={
																		detailsStyles[
																			`business-unit-status-${data?.taskStatus
																				?.toLowerCase()
																				.replaceAll(/ /g, '')}`
																		]
																	}
																/>
															</Box>
														))}
												</Card>
											</Popper>
										</>
									</Typography>
								)}
								{taskId && (
									<Stack direction="row" alignItems="center">
										<ButtonComp
											btnType="tertiary"
											startIcon={<Comment fill={commentIconColor} />}
											clickHandler={onClickComment}
											compClass={detailsStyles['inventory-comments-section']}
										>
											Comments
										</ButtonComp>
									</Stack>
								)}
							</Stack>
						</ClickAwayListener>
					</Grid>
				</Grid>
			</Banner>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<StepperProgressBar
					disabled
					status={status}
					progressBarElements={progressBarElements}
					aria-label="Progress-Bar"
					step={null}
					valueLabelDisplay="off"
				/>
			</Grid>
			<SideDrawer
				className={detailsStyles['side-drawer']}
				drawerState={isCommentOpen}
			>
				<Box
					data-testid="side-drawer-content"
					className={detailsStyles['detailed-container']}
					role="presentation"
				>
					<div className={detailsStyles['control-lay-text-bg']}>
						<Stack
							direction="row"
							justifyContent="flex-end"
							spacing={2}
							data-testid="close-button-stack"
						>
							<IconButton
								size="large"
								onClick={handleDrawerChange}
								data-testid="close-button-icon"
								className={detailsStyles['fileupload-close']}
							>
								<DrawerClose fill={drawerCloseIconColor} />
							</IconButton>
						</Stack>

						<Box className={detailsStyles['fileupload-text-box']}>
							<Typography className={detailsStyles['sidedrawer-title']}>
								{`Comments (${commentCount})`}
							</Typography>
							<Typography className={detailsStyles['sidedrawer-subtitle']}>
								{title}
							</Typography>
						</Box>
					</div>
					<Grid
						className={detailsStyles['comment-background']}
						item
						xs={12}
						sm={12}
						md={12}
						xl={12}
						lg={12}
						p={1}
					>
						<TaskComment taskId={taskId} type="standard" taskStatus={status} />
					</Grid>
				</Box>
			</SideDrawer>
		</>
	);
};
export default RegulationDetailsHeader;
